import { Moon, Sun, LogOut, ChevronDown, Key, Eye, EyeOff, X } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Header = ({ user, onLogout, onPasswordChanged }) => {
  const [darkMode, setDarkMode] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [appSettings, setAppSettings] = useState({
    app_title: 'GB-Sylt-Wissen',
    app_subtitle: 'Wissens- & Chat-System'
  });
  
  // Password change modal state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  useEffect(() => {
    const isDark = localStorage.getItem('darkMode') === 'true' ||
      (!localStorage.getItem('darkMode') && window.matchMedia('(prefers-color-scheme: dark)').matches);
    setDarkMode(isDark);
    if (isDark) {
      document.documentElement.classList.add('dark');
    }
    loadAppSettings();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadAppSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setAppSettings({
        app_title: response.data.app_title || 'GB-Sylt-Wissen',
        app_subtitle: response.data.app_subtitle || 'Wissens- & Chat-System'
      });
    } catch (error) {
      console.error('Fehler beim Laden der App-Einstellungen:', error);
    }
  };

  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem('darkMode', newMode.toString());
    if (newMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const getUserInitials = (name) => {
    if (!name) return 'G';
    return name.split(' ').map(part => part[0]).join('').toUpperCase().substring(0, 2);
  };

  const getRoleLabel = (role) => {
    const roles = { admin: 'Administrator', employee: 'Mitarbeiter', guest: 'Gast' };
    return roles[role] || role;
  };

  const handleLogout = () => {
    setDropdownOpen(false);
    onLogout();
  };

  const getToken = () => {
    const savedUser = localStorage.getItem('gb-sylt-user');
    if (savedUser) {
      try {
        const parsed = JSON.parse(savedUser);
        return parsed.token;
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  const openPasswordModal = () => {
    setDropdownOpen(false);
    setShowPasswordModal(true);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setPasswordSuccess('');
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setPasswordSuccess('');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    // Validation
    if (newPassword.length < 6) {
      setPasswordError('Neues Passwort muss mindestens 6 Zeichen haben');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwörter stimmen nicht überein');
      return;
    }

    setPasswordLoading(true);
    try {
      const token = getToken();
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setPasswordSuccess('Passwort erfolgreich geändert!');
      setTimeout(() => {
        closePasswordModal();
        if (onPasswordChanged) {
          onPasswordChanged();
        }
      }, 1500);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Fehler beim Ändern des Passworts';
      setPasswordError(msg);
    } finally {
      setPasswordLoading(false);
    }
  };

  const displayName = user?.name || 'Gast';
  const displayRole = user?.role || 'guest';
  const isGuest = displayRole === 'guest';

  return (
    <header className="h-16 border-b bg-white dark:bg-gray-900 flex items-center justify-between px-4 lg:px-6">
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-blue-600 flex items-center justify-center">
            <span className="text-white font-bold text-lg">GB</span>
          </div>
          <div className="hidden sm:block">
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">{appSettings.app_title}</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">{appSettings.app_subtitle}</p>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {/* Dark mode toggle - BOTH icons always rendered */}
        <button
          type="button"
          onClick={toggleDarkMode}
          className="h-9 w-9 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300 relative"
          aria-label="Dark Mode umschalten"
        >
          <Sun 
            className="h-5 w-5 absolute transition-opacity duration-200" 
            style={{ opacity: darkMode ? 1 : 0 }}
          />
          <Moon 
            className="h-5 w-5 absolute transition-opacity duration-200" 
            style={{ opacity: darkMode ? 0 : 1 }}
          />
        </button>

        {/* User menu */}
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="flex items-center gap-2 h-9 px-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center">
              <span className="text-white text-sm font-medium">{getUserInitials(displayName)}</span>
            </div>
            <span className="hidden md:inline-block text-sm font-medium text-gray-700 dark:text-gray-200">
              {displayName}
            </span>
            <ChevronDown 
              className="h-4 w-4 text-gray-500 transition-transform duration-200"
              style={{ transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
            />
          </button>

          {/* Dropdown - ALWAYS in DOM */}
          <div 
            className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50 transition-all duration-200"
            style={{
              opacity: dropdownOpen ? 1 : 0,
              visibility: dropdownOpen ? 'visible' : 'hidden',
              transform: dropdownOpen ? 'translateY(0)' : 'translateY(-8px)'
            }}
          >
            <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
              <p className="text-sm font-medium text-gray-900 dark:text-white">{displayName}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{getRoleLabel(displayRole)}</p>
            </div>
            {!isGuest && (
              <button
                type="button"
                onClick={openPasswordModal}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
              >
                <Key className="h-4 w-4" />
                Passwort ändern
              </button>
            )}
            <button
              type="button"
              onClick={handleLogout}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
            >
              <LogOut className="h-4 w-4" />
              Abmelden
            </button>
          </div>
        </div>
      </div>

      {/* Password Change Modal */}
      <div
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 transition-opacity duration-200"
        style={{
          opacity: showPasswordModal ? 1 : 0,
          visibility: showPasswordModal ? 'visible' : 'hidden'
        }}
        onClick={(e) => e.target === e.currentTarget && closePasswordModal()}
      >
        <div 
          className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md mx-4 transition-transform duration-200"
          style={{
            transform: showPasswordModal ? 'scale(1)' : 'scale(0.95)'
          }}
        >
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Passwort ändern</h2>
            <button
              type="button"
              onClick={closePasswordModal}
              className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          <form onSubmit={handleChangePassword} className="p-4 space-y-4">
            {passwordError && (
              <div className="p-3 rounded-lg bg-red-100 border border-red-200 text-red-700 text-sm">
                {passwordError}
              </div>
            )}
            {passwordSuccess && (
              <div className="p-3 rounded-lg bg-green-100 border border-green-200 text-green-700 text-sm">
                {passwordSuccess}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Aktuelles Passwort
              </label>
              <div className="relative">
                <input
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700"
                >
                  {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Neues Passwort
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700"
                >
                  {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Mindestens 6 Zeichen</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Neues Passwort bestätigen
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={closePasswordModal}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                disabled={passwordLoading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {passwordLoading ? 'Wird gespeichert...' : 'Speichern'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </header>
  );
};
